import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Package, ChevronRight, LogIn } from "lucide-react";

export const Route = createFileRoute("/orders")({
  head: () => ({
    meta: [
      { title: "Mes commandes — angle-shop.com" },
      { name: "description", content: "Consultez l'historique de vos commandes angle-shop.com." },
    ],
  }),
  component: OrdersPage,
});

interface Order {
  id: string;
  status: string;
  total_amount: number;
  items: any;
  shipping_address: any;
  created_at: string;
  email: string;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800" },
  paid: { label: "Payée", color: "bg-green-100 text-green-800" },
  shipped: { label: "Expédiée", color: "bg-blue-100 text-blue-800" },
  delivered: { label: "Livrée", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-800" },
};

function OrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (!error && data) {
        setOrders(data as Order[]);
      }
      setLoading(false);
    };

    fetchOrders();
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <main className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="min-h-screen bg-background">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <LogIn className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Connectez-vous</h2>
          <p className="text-muted-foreground mb-6">
            Vous devez être connecté pour voir vos commandes.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-electric text-electric-foreground font-medium hover:opacity-90 transition-opacity"
          >
            Se connecter
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold text-foreground mb-8">Mes commandes</h1>

        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-muted-foreground/40 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Aucune commande</h2>
            <p className="text-muted-foreground mb-6">Vous n'avez pas encore passé de commande.</p>
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-electric text-electric-foreground font-medium hover:opacity-90 transition-opacity"
            >
              Découvrir nos produits
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const items = Array.isArray(order.items) ? order.items : [];
              const status = statusLabels[order.status] || { label: order.status, color: "bg-muted text-muted-foreground" };
              const date = new Date(order.created_at).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              });

              return (
                <div
                  key={order.id}
                  className="border border-border rounded-2xl p-5 bg-surface-elevated hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Commande du {date}
                      </p>
                      <p className="text-xs text-muted-foreground/60 font-mono mt-0.5">
                        #{order.id.slice(0, 8)}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="space-y-2 mb-3">
                    {items.map((item: any, i: number) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-foreground">
                          {item.name || item.productName || "Article"} × {item.quantity || 1}
                        </span>
                        <span className="text-muted-foreground">
                          {((item.unitPrice || item.price || 0) * (item.quantity || 1)).toFixed(2)} €
                        </span>
                      </div>
                    ))}
                  </div>

                  {order.shipping_address && (
                    <div className="mb-3 p-3 bg-muted/30 rounded-xl">
                      <p className="text-xs font-medium text-muted-foreground mb-1">📦 Adresse de livraison</p>
                      <p className="text-sm text-foreground">
                        {order.shipping_address.name && <>{order.shipping_address.name}<br /></>}
                        {order.shipping_address.line1 || order.shipping_address.address?.line1}
                        {(order.shipping_address.line2 || order.shipping_address.address?.line2) && <><br />{order.shipping_address.line2 || order.shipping_address.address?.line2}</>}
                        <br />
                        {order.shipping_address.postal_code || order.shipping_address.address?.postal_code}{" "}
                        {order.shipping_address.city || order.shipping_address.address?.city}
                        {(order.shipping_address.country || order.shipping_address.address?.country) && <><br />{order.shipping_address.country || order.shipping_address.address?.country}</>}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-sm font-semibold text-foreground">
                      Total : {Number(order.total_amount).toFixed(2)} €
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
